import { NavLink, useMatch } from 'react-router-dom';
import {
  Nav,
  NavBody,
  NavFooter,
  NavGroup,
  NavGroupContent,
  NavGroupTrigger,
  NavItem,
  NavList,
  NavSeparator,
  Sidebar,
  SidebarToggle,
} from '@optiaxiom/react';
import { IconArrowsRepeat, IconChevronLeft, IconPuzzlePiece } from '@optimizely/axiom-icons';

import { NavigationItem } from '@frontend/components/ui/NavigationItem';
import { useBadgeCount } from '@frontend/features/notifications/hooks';
import { useLocalStorage, useOperations, useUnreadThread, useUxFeatures } from '@frontend/hooks';
import { InstanceOperationsName, OperationsName } from '@frontend/types';
import { isDesktopApp } from '@frontend/utils/desktop';

import {
  IconAgentRegular,
  IconBellRegular,
  IconBookRegular,
  IconBooksSolid,
  IconCommentsRegular,
  IconDatabaseRegular,
  IconFolderBookmarkRegular,
  IconGearRegular,
  IconScrewdriverWrenchRegular,
} from '../icons';

type RouteSection = 'top' | 'config' | 'footer';

type Route = {
  path: string;
  icon: JSX.Element;
  label: string;
  permissions: OperationsName[];
  instanceCapabilities?: InstanceOperationsName[];
  featureFlag?: string;
  section: RouteSection;
};

export const LeftNavbar = () => {
  const { canUser, canInstance } = useOperations();
  const [sidebarExpanded, setSidebarExpanded] = useLocalStorage('sidebar-expanded', false);
  const { hasUnreadThreads } = useUnreadThread();
  // Determine if the current route is the chat route
  const isOnChatRoute = useMatch('/instances/:instanceId/chat');

  const { reorgRagContext } = useUxFeatures();

  const { data: unreadNotificationCount } = useBadgeCount({ grouped: true });

  const routes: Route[] = [
    {
      path: 'chat',
      icon: <IconCommentsRegular />,
      label: 'Chats',
      permissions: ['use_chat'],
      section: 'top',
    },
    {
      path: 'artifacts',
      icon: <IconBooksSolid />,
      label: 'Artifacts',
      permissions: ['use_chat'],
      featureFlag: 'opal_artifacts',
      section: 'top',
    },
    {
      path: 'notifications',
      icon: <IconBellRegular />,
      label: 'Notifications',
      permissions: ['use_chat'], // TODO: support empty permissions array for truly public pages
      section: 'top',
    },
    {
      path: 'context',
      icon: <IconFolderBookmarkRegular />,
      label: 'Context',
      // We should add view_context or similar permission
      permissions: ['view_instructions', 'manage_instructions', 'use_chat', 'manage_rag_config'],
      section: 'config',
    },
    {
      path: 'instructions',
      icon: <IconBookRegular />,
      label: 'Instructions',
      permissions: ['view_instructions', 'manage_instructions', 'use_chat'],
      section: 'config',
    },
    {
      path: 'agents',
      icon: <IconAgentRegular />,
      label: 'Agents',
      permissions: ['view_agents'],
      section: 'config',
    },
    {
      path: 'tools',
      icon: <IconScrewdriverWrenchRegular />,
      label: 'Tools',
      permissions: ['view_tools', 'use_chat'],
      section: 'config',
    },
    {
      path: 'rag',
      icon: <IconDatabaseRegular />,
      label: 'RAG',
      permissions: ['manage_rag_config'],
      instanceCapabilities: ['RAG_enabled'],
      section: 'config',
    },
    {
      path: 'plugins',
      icon: <IconPuzzlePiece />,
      label: 'Plugins',
      permissions: ['use_chat'],
      featureFlag: 'plugins',
      section: 'config',
    },
    {
      path: 'settings',
      icon: <IconGearRegular />,
      label: 'Settings',
      permissions: ['use_chat'],
      section: 'config',
    },
  ];

  const shouldShowIndicator = (route: Route) => {
    if (route.path === 'chat') return hasUnreadThreads && !isOnChatRoute;
    if (route.path === 'notifications') return (unreadNotificationCount ?? 0) > 0;
    return false;
  };

  const renderNavItem = (route: Route) => (
    <NavigationItem
      key={route.path}
      path={route.path}
      icon={route.icon}
      label={route.label}
      permissions={route.permissions}
      instanceCapabilities={route.instanceCapabilities}
      featureFlag={route.featureFlag}
      showIndicator={shouldShowIndicator(route)}
    />
  );

  const renderNavItems = ({ section }: { section: RouteSection }) =>
    routes
      .filter(route => {
        if (route.section !== section) return false;

        // Check permission
        if (!canUser(route.permissions)) return false;

        // Check instance capabilities
        if (route.instanceCapabilities && !canInstance(route.instanceCapabilities)) return false;

        // Hide RAG & Instructions tabs when reorg_rag_context is enabled
        if ((route.path === 'rag' || route.path === 'instructions') && reorgRagContext)
          return false;

        // Hide Context tab when reorg_rag_context is disabled
        if (route.path === 'context' && !reorgRagContext) return false;

        return true;
      })
      .map(renderNavItem);

  const configItems = renderNavItems({ section: 'config' });

  return (
    <Sidebar defaultExpanded={sidebarExpanded} onExpandedChange={setSidebarExpanded}>
      <Nav>
        <NavBody>
          <NavList>{renderNavItems({ section: 'top' })}</NavList>
          {configItems.length > 0 && (
            <NavGroup>
              <NavSeparator />
              {sidebarExpanded && <NavGroupTrigger>Configurations</NavGroupTrigger>}
              <NavGroupContent>
                <NavList>{configItems}</NavList>
              </NavGroupContent>
            </NavGroup>
          )}
        </NavBody>

        <NavFooter>
          <NavList>
            {isDesktopApp() && (
              <NavLink to="/instances" style={{ textDecoration: 'none', color: 'inherit' }}>
                <NavItem icon={<IconArrowsRepeat />}>Switch Instance</NavItem>
              </NavLink>
            )}
            <SidebarToggle icon={<IconChevronLeft />} />
            {renderNavItems({ section: 'footer' })}
          </NavList>
        </NavFooter>
      </Nav>
    </Sidebar>
  );
};
